import type { FieldOp, RecordOp, RecordsDiffMessage } from "./protocol";

type Json = Record<string, unknown>;
const isObject = (value: unknown): value is Json =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const same = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Field-level patch from `prev` to `next`. Plain objects are patched key by key
 * (so concurrent edits to different props merge); arrays and values are replaced.
 */
export function diffFields(prev: Json, next: Json): Record<string, FieldOp> {
  const ops: Record<string, FieldOp> = {};
  for (const key of Object.keys(next)) {
    const before = prev[key],
      after = next[key];
    if (same(before, after)) continue;
    ops[key] =
      isObject(before) && isObject(after)
        ? ["patch", diffFields(before, after)]
        : ["put", after];
  }
  for (const key of Object.keys(prev))
    if (!(key in next)) ops[key] = ["delete"];
  return ops;
}

export function applyFields<T extends Json>(
  target: T,
  ops: Record<string, FieldOp>,
): T {
  const out: Json = { ...target };
  for (const [key, op] of Object.entries(ops)) {
    if (!Array.isArray(op)) throw new Error("Invalid field change");
    if (op[0] === "put") out[key] = op[1];
    else if (op[0] === "delete") delete out[key];
    else if (op[0] === "patch") {
      if (!isObject(out[key]) || !isObject(op[1]))
        throw new Error("Invalid nested change");
      out[key] = applyFields(
        out[key] as Json,
        op[1] as Record<string, FieldOp>,
      );
    } else throw new Error("Invalid field change");
  }
  return out as T;
}

/** The record after applying `op` to `current`; null when removed. */
export function applyRecordOp<T extends object>(
  current: T | undefined,
  op: RecordOp,
): T | null {
  if (!Array.isArray(op)) throw new Error("Invalid change");
  if (op[0] === "put") return op[1] as T;
  if (op[0] === "remove") return null;
  if (op[0] === "patch") {
    if (!current) throw new Error("Cannot patch a missing record");
    return applyFields(current as Json, op[1]) as T;
  }
  throw new Error("Invalid change");
}

/** Full-record puts and removals for a set of final states (null = removed). */
export function putsAndRemoves(
  records: Iterable<[string, object | null]>,
): RecordsDiffMessage {
  const diff: RecordsDiffMessage = {};
  for (const [id, record] of records)
    diff[id] = record ? ["put", record as Json] : ["remove"];
  return diff;
}
